import type { IBusinessProfile } from '@optimus/models';
import type {
  AgentBusinessProfile,
  AgentBusinessStageCode,
} from '../../api/coachAPI';
import {
  AnnualRevenueRange,
  BusinessStage,
} from '../enums';

// The UI collects revenue bands while the agent contract requires one numeric
// amount. Use the midpoint for bounded bands and the lower bound for $5M+.
const annualRevenueAmounts: Record<string, number> = {
  [AnnualRevenueRange.Under100K]: 50_000,
  [AnnualRevenueRange.Between100KAnd250K]: 175_000,
  [AnnualRevenueRange.Between250KAnd500K]: 375_000,
  [AnnualRevenueRange.Between500KAnd1M]: 750_000,
  [AnnualRevenueRange.Between1MAnd5M]: 3_000_000,
  [AnnualRevenueRange.Over5M]: 5_000_000,
};

const businessStageCodes: Record<string, AgentBusinessStageCode> = {
  [BusinessStage.Foundation]: 'FOUNDATIONAL',
  [BusinessStage.Growth]: 'INITIAL_GROWTH',
  [BusinessStage.Scaling]: 'SCALING',
  [BusinessStage.Mature]: 'MATURE',
};

/**
 * Converts the profile persisted for the Business Profile UI into the exact
 * agent-specific parameter shape accepted by the Agents Gateway.
 */
export const buildBusinessProfileAgentParams = (
  profile: IBusinessProfile | null,
  tierCode?: string,
): AgentBusinessProfile | undefined => {
  if (!profile) return undefined;

  const params: AgentBusinessProfile = {};

  if (profile.primaryTrade) params.tradeIndustry = profile.primaryTrade;
  if (profile.officeEmployeeCount !== null) {
    params.internalEmployeeCount = profile.officeEmployeeCount;
  }
  if (profile.fieldEmployeeCount !== null) {
    params.fieldEmployeeCount = profile.fieldEmployeeCount;
  }
  if (profile.annualRevenueRange) {
    const amount = annualRevenueAmounts[profile.annualRevenueRange];
    if (amount !== undefined) {
      params.annualRevenue = { amount, currency: 'USD' };
    }
  }
  if (tierCode) params.tierCode = tierCode;
  if (profile.businessStage) {
    const code = businessStageCodes[profile.businessStage];
    if (code) params.businessStageCode = code;
  }

  return Object.keys(params).length > 0 ? params : undefined;
};
